import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { ComposicaoKitFotovoltaicoForm } from "@/constants/adequacoesPropostaAtividades";
import type { Inversor } from "@/types/inversores";
import type { ModuloFotovoltaico } from "@/types/modulos";

type KitFotovoltaicoCamposProps = {
  value: ComposicaoKitFotovoltaicoForm;
  onChange: (patch: Partial<ComposicaoKitFotovoltaicoForm>) => void;
  modulos: ModuloFotovoltaico[];
  inversores: Inversor[];
  carregando: boolean;
};

function marcasUnicas(itens: readonly { marca: string }[]): string[] {
  return [...new Set(itens.map((i) => i.marca).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, "pt-BR", { sensitivity: "base" })
  );
}

function alterarQuantidade(
  raw: string,
  onChange: (patch: Partial<ComposicaoKitFotovoltaicoForm>) => void,
  campo: "quantidadeModulos" | "quantidadeInversores"
) {
  if (raw === "") {
    onChange({ [campo]: "" });
    return;
  }
  if (!/^\d+$/.test(raw)) return;
  if (parseInt(raw, 10) < 1) return;
  onChange({ [campo]: raw });
}

export default function KitFotovoltaicoCampos({
  value,
  onChange,
  modulos,
  inversores,
  carregando,
}: KitFotovoltaicoCamposProps) {
  const marcasModulo = useMemo(() => marcasUnicas(modulos), [modulos]);
  const marcasInversor = useMemo(() => marcasUnicas(inversores), [inversores]);
  const modelosModulo = useMemo(
    () => modulos.filter((m) => m.marca === value.marcaModulo),
    [modulos, value.marcaModulo]
  );
  const modelosInversor = useMemo(
    () => inversores.filter((i) => i.marca === value.marcaInversor),
    [inversores, value.marcaInversor]
  );

  return (
    <div className="mt-3 space-y-4 rounded-lg border border-dashed bg-muted/20 p-3">
      <div className="space-y-1">
        <p className="text-sm font-medium">Composição do kit</p>
        <p className="text-sm text-muted-foreground">
          Módulos, inversor(es) e cabos CC estão inclusos. O modelo da placa vem do cadastro de Módulos
          fotovoltaicos; o inversor, do cadastro de Inversores e Micro.
        </p>
      </div>
      {carregando ? (
        <p className="text-sm text-muted-foreground">Carregando módulos e inversores…</p>
      ) : null}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="kit-fv-marca-modulo">Marca da placa</Label>
          <Select
            value={value.marcaModulo || undefined}
            onValueChange={(marca) => onChange({ marcaModulo: marca, moduloId: "" })}
            disabled={carregando || marcasModulo.length === 0}
          >
            <SelectTrigger id="kit-fv-marca-modulo">
              <SelectValue placeholder="Selecione a marca" />
            </SelectTrigger>
            <SelectContent>
              {marcasModulo.map((marca) => (
                <SelectItem key={marca} value={marca}>
                  {marca}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="kit-fv-modelo-modulo">Modelo da placa</Label>
          <Select
            value={value.moduloId || undefined}
            onValueChange={(moduloId) => onChange({ moduloId })}
            disabled={!value.marcaModulo || modelosModulo.length === 0}
          >
            <SelectTrigger id="kit-fv-modelo-modulo">
              <SelectValue placeholder="Selecione o modelo" />
            </SelectTrigger>
            <SelectContent>
              {modelosModulo.map((modulo) => (
                <SelectItem key={modulo.id} value={modulo.id}>
                  {modulo.modelo} ({modulo.potencia} W)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="kit-fv-qtd-modulos">Quantidade de placas</Label>
          <Input
            id="kit-fv-qtd-modulos"
            type="number"
            min={1}
            step={1}
            value={value.quantidadeModulos}
            onChange={(e) => alterarQuantidade(e.target.value, onChange, "quantidadeModulos")}
          />
        </div>
      </div>
      {!carregando && modulos.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Nenhum módulo cadastrado. Inclua placas na página Módulos fotovoltaicos.
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="kit-fv-marca-inversor">Marca do inversor</Label>
          <Select
            value={value.marcaInversor || undefined}
            onValueChange={(marca) => onChange({ marcaInversor: marca, inversorId: "" })}
            disabled={carregando || marcasInversor.length === 0}
          >
            <SelectTrigger id="kit-fv-marca-inversor">
              <SelectValue placeholder="Selecione a marca" />
            </SelectTrigger>
            <SelectContent>
              {marcasInversor.map((marca) => (
                <SelectItem key={marca} value={marca}>
                  {marca}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="kit-fv-modelo-inversor">Modelo do inversor</Label>
          <Select
            value={value.inversorId || undefined}
            onValueChange={(inversorId) => onChange({ inversorId })}
            disabled={!value.marcaInversor || modelosInversor.length === 0}
          >
            <SelectTrigger id="kit-fv-modelo-inversor">
              <SelectValue placeholder="Selecione o modelo" />
            </SelectTrigger>
            <SelectContent>
              {modelosInversor.map((inversor) => (
                <SelectItem key={inversor.id} value={inversor.id}>
                  {inversor.modelo} ({inversor.potenciaNominal} W)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="kit-fv-qtd-inversores">Quantidade de inversores</Label>
          <Input
            id="kit-fv-qtd-inversores"
            type="number"
            min={1}
            step={1}
            value={value.quantidadeInversores}
            onChange={(e) => alterarQuantidade(e.target.value, onChange, "quantidadeInversores")}
          />
        </div>
      </div>
      {!carregando && inversores.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Nenhum inversor cadastrado. Inclua equipamentos na página Inversores e Micro.
        </p>
      ) : null}
    </div>
  );
}
